import { SectionHeader } from "@/components/common/section-components/SectionHeader";
import { InputField } from "@/components/input-field/InputField";
import { AppRoutes } from "@/constants/routes";
import { AppColors } from "@/styles/colors";
import { isEmpty } from "@/utils/query-string";
import { Card, Flex, Grid, GridItem } from "@chakra-ui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useMemo, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useNavigate } from "react-router-dom";

import { Hobbie, relationFieldSchema } from "../schema/relation-fields.schema";
import { getHobbiesSimilares } from "@/services/relation-fields.service";
import { SimilarRelationFieldsAlert } from "@/components/common/section-components/alert/SimilarRelationFieldsAlert";
import { handleCreateHobbieHandler } from "../schema/handler-functions";
import { useRoles } from "@/hooks/useRoles";

export const AddHobbiePage = () => {
  const { control } = useForm({
    resolver: zodResolver(relationFieldSchema),
  });

  const watchedValues = useWatch({
    control,
  });

  const hasNoFilledValues = useMemo(() => {
    const { nombre } = watchedValues || {};

    return isEmpty(nombre);
  }, [watchedValues]);

  const [loadingCreateHobbie, setLoadingCreateHobbie] =
    useState<boolean>(false);
  const navigate = useNavigate();
  const { fetchPermissions } = useRoles();
  const handleCancel = () => {
    navigate(AppRoutes.RELATION_FIELDS);
  };

  const [similarHobbies, setSimilarHobbies] = useState<Hobbie[] | undefined>(
    [],
  );

  const handleCreateHobbie = () => {
    handleCreateHobbieHandler({
      setLoadingCreateHobbie,
      navigate,
      fetchPermissions,
      watchedValues,
    });
  };

  const [loadingSimilarHobbies, setLoadingSimilarHobbies] =
    useState<boolean>(false);

  const getSimilarHobbies = async (nombre: string) => {
    setLoadingSimilarHobbies(true);
    const response = await getHobbiesSimilares({
      nombre,
    });

    setSimilarHobbies(response);

    setLoadingSimilarHobbies(false);
  };

  useEffect(() => {
    const { nombre } = watchedValues || {};
    if (!nombre) return;

    const timeout = setTimeout(() => {
      getSimilarHobbies(nombre);
    }, 500);

    return () => clearTimeout(timeout);
  }, [watchedValues?.nombre]);

  return (
    <Flex flexDir="column" paddingTop="20px" gap="12px">
      <SectionHeader
        title="Agregar hobbie"
        addButtonLabel="Agregar"
        cancelButtonLabel="Cancelar"
        handleCancel={handleCancel}
        handleAdd={handleCreateHobbie}
        addButtonType="submit"
        disableAdd={hasNoFilledValues || loadingCreateHobbie}
        loading={loadingCreateHobbie}
      />

      <Card.Root maxW="full" marginTop="30px" borderRadius="8px">
        <Card.Body
          paddingX="48px"
          paddingY="30px"
          borderRadius="8px"
          border={`1px solid ${AppColors.WHITE_BORDER_1}`}
        >
          <Grid templateColumns="repeat(3, 1fr)">
            <GridItem paddingX="16px" paddingY="8px">
              <InputField
                inputName="nombre"
                labelText="Nombre"
                control={control}
                disabled={loadingCreateHobbie}
                placeholder="Ingresar nombre del hobbie"
              />
            </GridItem>
          </Grid>
        </Card.Body>
      </Card.Root>

      {/* Hobbies similares */}
      {Boolean(similarHobbies?.length) && !isEmpty(watchedValues?.nombre) && (
        <SimilarRelationFieldsAlert
          title="Hay hobbies similares existentes"
          legend="Evite repeticiones"
          similarItems={similarHobbies?.map((el) => el.nombre) || []}
          loadingSimilarElements={loadingSimilarHobbies}
        />
      )}
    </Flex>
  );
};
